import { Children, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './RollingBanner.module.scss';

interface RollingBannerProps {
  speed?: number; // 속도 (픽셀/초)
  reverse?: boolean; // 역방향 재생 여부
  children?: React.ReactNode | React.ReactNode[];
  gradientColor?: string;
  gradient?: boolean;
  gradientWidth?: number | string;
  direction?: 'row' | 'column';
}

export default function RollingBanner({
  speed = 50, gradient = true, gradientColor = 'white', gradientWidth = 200,
  reverse = false, children, direction = 'row' }: RollingBannerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const marqueeRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [marqueeWidth, setMarqueeWidth] = useState(0);
  const [repeat, setRepeat] = useState(1);

  const calculateWidth = useCallback(() => {
    if (marqueeRef.current && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const marqueeRect = marqueeRef.current.getBoundingClientRect();

      // direction에 따라 width 또는 height 계산
      const containerSize = direction === 'row' ? containerRect.width : containerRect.height;
      const marqueeSize = direction === 'row' ? marqueeRect.width : marqueeRect.height;

      if (containerSize && marqueeSize) {
        setRepeat(
          marqueeSize < containerSize
            ? Math.ceil(containerSize / marqueeSize)
            : 1,
        );
      } else {
        setRepeat(1);
      }

      setMarqueeWidth(marqueeSize);
    }
  }, [containerRef, direction]);

  const duration = useMemo(() => {
    const calculatedDuration = (marqueeWidth * repeat) / speed;
    // duration이 0이거나 유효하지 않으면 기본값 사용
    return calculatedDuration > 0 && Number.isFinite(calculatedDuration) ? calculatedDuration : 10;
  }, [marqueeWidth, repeat, speed]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    calculateWidth();
    if (marqueeRef.current && containerRef.current) {
      const resizeObserver = new ResizeObserver(() => calculateWidth());
      resizeObserver.observe(containerRef.current);
      resizeObserver.observe(marqueeRef.current);
      return () => {
        if (!resizeObserver) return;
        resizeObserver.disconnect();
      };
    }
  }, [calculateWidth, containerRef, isMounted]);

  useEffect(() => {
    calculateWidth();
  }, [calculateWidth, children]);

  const marqueeStyle = useMemo(
    () => {
      const style = {
        ['--direction' as string]: reverse ? 'reverse' : 'normal',
        ['--duration' as string]: `${duration}s`,
        ['--flex-direction' as string]: direction,
        ['--animation-name' as string]: direction === 'column' ? 'scroll-column' : 'scroll',
      };

      return style;
    },
    [reverse, duration, direction],
  );

  const repeatChildren = useCallback((repeat: number) => {
    return [
      ...Array(Number.isFinite(repeat) && repeat >= 0 ? repeat : 0),
    ].map((_, index) => (
      <Fragment key={index}>
        {Children.map(children, (child) => (
          <div className={styles.child}>
            {child}
          </div>
        ))}
      </Fragment>
    ));
  }, [children]);

  const gradientStyle = useMemo(
    () => ({
      ['--gradient-color' as string]: gradientColor,
      ['--gradient-width' as string]:
        typeof gradientWidth === 'number'
          ? `${gradientWidth}px`
          : gradientWidth,
    }),
    [gradientColor, gradientWidth]);

  return (
    <div
      className={`${styles['marquee-container']} ${direction === 'column' ? styles['marquee-container--column'] : ''}`}
      ref={containerRef}
    >
      {gradient && <div style={gradientStyle} className={`${styles.overlay} ${direction === 'column' ? styles['overlay--column'] : ''}`} />}
      <div
        className={`${styles.marquee} ${direction === 'column' ? styles['marquee--column'] : ''}`}
        style={marqueeStyle}
      >
        <div className={styles['initial-child-container']} ref={marqueeRef} style={marqueeStyle}>
          {Children.map(children, (child) => {
            return (
              <div className={styles.child}>
                {child}
              </div>
            );
          })}
        </div>
        {repeatChildren(repeat - 1)}
      </div>
      <div className={`${styles.marquee} ${direction === 'column' ? styles['marquee--column'] : ''}`} style={marqueeStyle}>
        {repeatChildren(repeat)}
      </div>

    </div>
  );
}